import { useMemo } from 'react';

import { CELL_GROUP_HEIGHT_PX } from '../../constants';
import { calculateAssignmentLevels } from '../../helpers';
import { getTimeOffRequiredRowHeight } from '../../helpers/assignment-timeoff-fragments';
import { getTimelineRowHeight } from '../../helpers/planner-base/timeline-row-height';
import usePlannerStore from '../../store';
import { AssignmentType, RowItem, RowItemType } from '../../types';

/**
 * Calculates heights for all rows based on their assignment levels and visible user info fields.
 * Recomputes when userInfoVisibility changes so the virtualizer always has correct estimates.
 *
 * A row also grows when a HALF/QUARTER time-off leaves too little room for that day's bars
 * beside the block (getTimeOffRequiredRowHeight). Every input here must match TimelineRow's
 * own cellHeight computation, or the virtualizer and the rendered row disagree.
 */
export const useRowHeights = (
  rows: RowItem[],
  assignmentsMap: Map<string, AssignmentType[]>
): Map<string, number> => {
  const userInfoVisibility = usePlannerStore(store => store.userInfoVisibility);
  const timeOffsMap = usePlannerStore(store => store.timeOffs);

  return useMemo(() => {
    const heights = new Map<string, number>();

    rows.forEach(row => {
      const key = row.parentGroupId ? `${row.parentGroupId}-${row.id}` : row.id;

      if (row.type === RowItemType.Group) {
        heights.set(key, CELL_GROUP_HEIGHT_PX);
      } else {
        const assignments = assignmentsMap.get(row.id);
        if (!assignments?.length) {
          heights.set(
            key,
            getTimelineRowHeight({ maxIntersectedAssignments: 1, userInfoVisibility })
          );
          return;
        }
        const { assignmentLevels, maxIntersectedAssignments } =
          calculateAssignmentLevels(assignments);
        const minHeightPx = getTimeOffRequiredRowHeight(
          assignments,
          assignmentLevels,
          timeOffsMap.get(row.id) ?? []
        );
        heights.set(
          key,
          getTimelineRowHeight({ maxIntersectedAssignments, userInfoVisibility, minHeightPx })
        );
      }
    });

    return heights;
  }, [rows, assignmentsMap, timeOffsMap, userInfoVisibility]);
};
